import { useState, useEffect } from "react";
import axios from "axios";
import lokult_logo_white from "../assets/lokult_logo_white.svg";
import { MdLogin, MdPerson, MdSearch, MdNotifications } from "react-icons/md";

const Navbar = ({ token }) => {
  const [notifications, setNotifications] = useState([]);
  const apiUrl = import.meta.env.VITE_API_URL;

  const unreadCount = notifications.filter((n) => !n.is_read).length;

  useEffect(() => {
    if (!token) return;

    const fetchNotifications = async () => {
      try {
        const response = await axios.get(`${apiUrl}notifications`, {
          withCredentials: true,
        });
        setNotifications(response.data);
      } catch (err) {
        console.error("Failed to fetch notifications:", err);
      }
    };

    fetchNotifications();
    const interval = setInterval(fetchNotifications, 30000);
    return () => clearInterval(interval);
  }, [token, apiUrl]);

  const handleDropdownOpen = async () => {
    if (notifications.some((n) => !n.is_read)) {
      try {
        await axios.put(
          `${apiUrl}notifications/read`,
          {},
          { withCredentials: true }
        );
        setNotifications((prev) =>
          prev.map((n) => ({ ...n, is_read: true }))
        );
      } catch (err) {
        console.error("Failed to mark notifications as read:", err);
      }
    }
  };

  const handleClearAll = async () => {
    try {
      await axios.delete(`${apiUrl}notifications`, {
        withCredentials: true,
      });
      setNotifications([]);
    } catch (err) {
      console.error("Failed to clear notifications:", err);
    }
  };

  return (
    <div className="z-20 top-0 max-w-7xl m-auto w-full ">
      <div className="navbar justify-between max-w-7xl m-auto text-white">
        <div className="navbar-start w-auto">
          <div className="dropdown">
            <div tabIndex={0} role="button" className="btn btn-ghost lg:hidden">
              <svg
                xmlns="http://www.w3.org/2000/svg"
                className="h-5 w-5"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth="2"
                  d="M4 6h16M4 12h8m-8 6h16"
                />
              </svg>
            </div>
            <ul
              tabIndex={0}
              className="menu menu-sm dropdown-content mt-3 z-[1] p-2 shadow bg-base-100 rounded-box w-60 space-y-8 pt-8"
            >
              <li>
                <a className="text-xl font-semibold" href="/discover">
                  Upptäck
                </a>
              </li>
              <li>
                <a className="text-xl font-semibold" href="/events">
                  Events
                </a>
              </li>
              <li>
                <a className="text-xl font-semibold" href="/news">
                  Nyheter
                </a>
              </li>
              <li>
                <a className="text-xl font-semibold" href="/blogs">
                  Bloggar
                </a>
              </li>
              <li>
                <a className="text-xl font-semibold" href="/playlist">
                  Spellista
                </a>
              </li>
              <li>
                <a className="text-xl font-semibold" href="/search">
                  <MdSearch size={24} />
                </a>
              </li>
              {!token && (
                <li className="font-semibold">
                  <a
                    className="btn btn-wide hover:bg-base-content/10 flex items-center"
                    href="/login"
                  >
                    <MdLogin size={20} /> Logga in
                  </a>
                </li>
              )}
            </ul>
          </div>
          <a href="/" className="h-14 w-24 place-content-center">
            <img src={lokult_logo_white} alt="Lokult logo" />
          </a>
        </div>
        <div className="navbar-center hidden lg:flex">
          <ul className="menu menu-horizontal px-1 font-semibold xl:space-x-10 space-x-3 text-xl tracking-wide">
            <li>
              <a href="/discover">Upptäck</a>
            </li>
            <li>
              <a href="/events">Events</a>
            </li>
            <li>
              <a href="/news">Nyheter</a>
            </li>
            <li>
              <a href="/blogs">Bloggar</a>
            </li>
            <li>
              <a href="/playlist">Spellista</a>
            </li>

            <li>
              <a href="/search" className="pt-2">
                <MdSearch size={24} />
              </a>
            </li>
          </ul>
        </div>
        <div className="navbar-end w-auto">
          {token ? (
            <>
              <div className="dropdown dropdown-end mr-1">
                <div
                  tabIndex={0}
                  role="button"
                  onClick={handleDropdownOpen}
                  className="btn btn-ghost btn-circle border border-base-content/20 relative"
                >
                  <MdNotifications size={22} className="text-white" />
                  {unreadCount > 0 && (
                    <span className="absolute -top-1 -right-1 flex h-5 w-5 items-center justify-center rounded-full bg-error text-white text-[10px] font-bold">
                      {unreadCount}
                    </span>
                  )}
                </div>
                <div
                  tabIndex={0}
                  className="card card-compact dropdown-content z-[30] mt-3 w-80 shadow bg-neutral border border-base-content/20 text-neutral-content"
                >
                  <div className="card-body">
                    <h3 className="font-bold text-lg border-b border-base-content/10 pb-2 text-white">
                      Notifikationer
                    </h3>
                    {notifications.length > 0 ? (
                      <div className="divide-y divide-base-content/10 text-white/90 max-h-60 overflow-y-auto">
                        {notifications.map((n) => (
                          <div key={n.id} className="py-2">
                            <p className="font-semibold text-sm">{n.title}</p>
                            <p className="text-xs text-white/60">{n.message}</p>
                          </div>
                        ))}
                      </div>
                    ) : (
                      <p className="text-sm text-white/50 py-4 text-center">
                        Inga nya notifikationer
                      </p>
                    )}
                    {notifications.length > 0 && (
                      <div className="card-actions justify-end border-t border-base-content/10 pt-2 mt-2">
                        <button
                          onClick={handleClearAll}
                          className="btn btn-ghost btn-xs text-error"
                        >
                          Rensa alla
                        </button>
                      </div>
                    )}
                  </div>
                </div>
              </div>
              <a
                href="/userprofile"
                className="btn btn-secondary border border-base-content/20 mx-2"
              >
                <MdPerson size={20} /> Min Profil
              </a>
            </>
          ) : (
            <div className="space-x-2 flex">
              <a
                href="/register"
                className="btn btn-primary border border-base-content/20"
              >
                Bli medlem
              </a>
              <a
                href="/login"
                className="btn hover:bg-base-content/10 hidden lg:flex items-center"
              >
                <MdLogin size={20} /> Logga in
              </a>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default Navbar;
